import { CartAddEvent } from '@theme/events';

const SHEET_ID = 'overcut-quick-add-sheet';

/**
 * Marks cart updates that came from this sheet. The cart drawer checks for it so
 * it stays shut: quick add is the keep-browsing flow, and confirms with a toast.
 */
const QUICK_ADD_SOURCE = 'overcut-quick-add';

/** @param {number} cents */
function money(cents) {
  const currency = window.Shopify?.currency?.active;
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  return currency === 'EUR' ? `${amount} €` : `${amount} ${currency || ''}`.trim();
}

const SWATCHES = {
  écru: '#f2efe6',
  ecru: '#f2efe6',
  corde: '#dad3c0',
  'french marine': '#002b46',
  marine: '#002b46',
  kaki: '#4d503d',
  blanc: '#ffffff',
  noir: '#1a1a1a',
  bordeaux: '#6c012f',
  rouge: '#c62828',
  orange: '#ef6052',
  jaune: '#f2c230',
  vert: '#2f6b45',
  'bleu ciel': '#7fc4d8',
  'bleu marine': '#002b46',
  'gris chiné': '#989792',
  'anthracite chiné': '#364242',
};

// Le storefront renvoie les noms de coloris TRADUITS dans la langue de la
// page (« Blanc » devient « Weiß » sur /de/). Une entree par famille, avec
// les racines des six langues publiees ; le marine passe avant le bleu
// generique, comme dans la chaine Liquid de overcut-home.liquid.
const SWATCH_KEYWORDS = [
  ['#002b46', ['marin', 'navy']],
  ['#f2efe6', ['cru', 'écru']],
  ['#dad3c0', ['corde', 'rope', 'kordel', 'cuerda']],
  ['#4d503d', ['kaki', 'khaki', 'caqui']],
  ['#1a1a1a', ['noir', 'black', 'schwarz', 'negro', 'nero', 'zwart']],
  ['#ffffff', ['blanc', 'white', 'weiß', 'weiss', 'bianco', 'wit']],
  ['#6c012f', ['bordeaux', 'burdeos', 'burgundy']],
  ['#c62828', ['rouge', 'red', 'rot', 'rojo', 'rosso', 'rood']],
  ['#2f6b45', ['vert', 'green', 'grün', 'gruen', 'verde', 'groen']],
  ['#ef6052', ['orange', 'naranja', 'aranci', 'oranje']],
  ['#f2c230', ['jaune', 'yellow', 'gelb', 'amarillo', 'giallo', 'geel']],
  ['#7fc4d8', ['bleu', 'blue', 'blau', 'azul', 'azzurro', 'ciel', 'sky', 'himmel', 'licht']],
  ['#989792', ['gris', 'grey', 'gray', 'grau', 'grigio', 'grijs']],
];

/** @param {string} name */
function swatchFor(name) {
  const key = name.trim().toLowerCase();
  if (SWATCHES[key]) return SWATCHES[key];
  for (const [hex, roots] of SWATCH_KEYWORDS) {
    if (roots.some((root) => key.includes(root))) return hex;
  }
  return null;
}

/** @param {string} name */
function isColorOption(name) {
  // couleur/color/colore + Farbe (de) + kleur (nl)
  return /couleur|color|coloris|teinte|farbe|kleur/i.test(name);
}

/**
 * Overcut quick add: one sheet for the whole page. Slides up from the bottom on
 * touch screens, sits centred on desktop. Every option of the product is
 * rendered as a row of targets no smaller than the theme's touch target.
 */
class OvercutQuickAddSheet extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-qa-body]');
    this.titleEl = this.querySelector('[data-qa-title]');
    this.vendorEl = this.querySelector('[data-qa-vendor]');
    this.priceEl = this.querySelector('[data-qa-price]');
    this.imageEl = this.querySelector('[data-qa-image]');
    // Trois elements pointent vers la fiche produit : l'image, le titre et le lien
    // "Voir la fiche complete". querySelector n'en servait qu'un, les deux autres
    // restaient sur leur href="#" de remplacement et ne menaient nulle part.
    this.linkEls = this.querySelectorAll('[data-qa-link]');
    this.submitEl = this.querySelector('[data-qa-submit]');
    this.hintEl = this.querySelector('[data-qa-hint]');

    this.close = this.close.bind(this);
    this.submit = this.submit.bind(this);

    this.querySelectorAll('[data-qa-close]').forEach((node) => node.addEventListener('click', this.close));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.submitEl?.addEventListener('click', this.submit);

    // Opens the shared size guide, preselecting the tab for this garment type.
    this.querySelector('[data-qa-size-guide]')?.addEventListener('click', () => {
      document.dispatchEvent(
        new CustomEvent('overcut-size-guide:open', {
          bubbles: true,
          detail: { tab: this.product?.type },
        })
      );
    });

    document.addEventListener('overcut-quick-add:open', (event) => {
      const handle = /** @type {CustomEvent} */ (event).detail?.handle;
      if (handle) this.open(handle);
    });
  }

  /** @param {string} handle */
  async open(handle) {
    this.selection = [];
    this.product = null;
    this.setHint(this.dataset.hintLoading || '');
    this.body?.replaceChildren();
    this.dialog?.showModal();

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${handle}.js`);
    if (!response.ok) return this.close();

    this.product = await response.json();
    this.render();
  }

  close() {
    this.dialog?.close();
  }

  /** @param {string} text @param {boolean} [isError] */
  setHint(text, isError) {
    if (!this.hintEl) return;
    this.hintEl.textContent = text;
    this.hintEl.classList.toggle('overcut-quick-add__hint--error', Boolean(isError));
  }

  render() {
    const product = this.product;
    if (!product || !this.body) return;

    this.selection = product.options.map(() => null);

    // preselect any option that only has one value
    product.options.forEach((option, index) => {
      const values = this.valuesFor(index);
      if (values.length === 1) this.selection[index] = values[0];
    });

    if (this.titleEl) this.titleEl.textContent = product.title;
    if (this.vendorEl) this.vendorEl.textContent = product.type || product.vendor || '';
    if (this.priceEl) this.priceEl.textContent = money(product.price);
    this.linkEls?.forEach((node) => node.setAttribute('href', product.url));
    if (this.imageEl && product.featured_image) {
      this.imageEl.src = product.featured_image;
      this.imageEl.alt = product.title;
    }

    this.body.replaceChildren();

    product.options.forEach((option, index) => {
      const name = typeof option === 'string' ? option : option.name;
      const group = document.createElement('div');
      group.className = 'overcut-quick-add__group';

      const label = document.createElement('p');
      label.className = 'overcut-quick-add__label';
      label.dataset.qaLabel = String(index);
      group.append(label);

      const list = document.createElement('div');
      list.className = isColorOption(name)
        ? 'overcut-quick-add__values overcut-quick-add__values--swatch'
        : 'overcut-quick-add__values';

      for (const value of this.valuesFor(index)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'overcut-quick-add__value';
        button.dataset.value = value;
        button.dataset.index = String(index);
        button.setAttribute('aria-pressed', 'false');

        const hex = isColorOption(name) ? swatchFor(value) : null;
        if (hex) {
          button.classList.add('overcut-quick-add__value--swatch');
          button.style.setProperty('--swatch', hex);
          button.setAttribute('aria-label', value);
          button.title = value;
        } else {
          button.textContent = value;
        }

        button.addEventListener('click', () => {
          this.selection[index] = this.selection[index] === value ? null : value;
          this.sync();
        });

        list.append(button);
      }

      group.append(list);
      this.body.append(group);
    });

    this.sync();
  }

  /** @param {number} index */
  valuesFor(index) {
    const seen = [];
    for (const variant of this.product?.variants || []) {
      const value = variant.options[index];
      if (value && !seen.includes(value)) seen.push(value);
    }
    return seen;
  }

  /** The variant matching every currently selected option, if any. */
  get variant() {
    if (!this.product) return null;
    if (this.selection.some((value) => value === null)) return null;
    return (
      this.product.variants.find((variant) =>
        variant.options.every((value, index) => value === this.selection[index])
      ) || null
    );
  }

  /**
   * A value stays selectable when at least one available variant carries it
   * alongside the other picked options.
   * @param {number} index @param {string} value
   */
  isAvailable(index, value) {
    return (this.product?.variants || []).some((variant) => {
      if (!variant.available) return false;
      if (variant.options[index] !== value) return false;
      return this.selection.every((picked, i) => i === index || picked === null || variant.options[i] === picked);
    });
  }

  sync() {
    const product = this.product;
    if (!product || !this.body) return;

    product.options.forEach((option, index) => {
      const name = typeof option === 'string' ? option : option.name;
      const picked = this.selection[index];
      const label = this.body.querySelector(`[data-qa-label="${index}"]`);
      if (label) label.textContent = picked ? `${name} : ${picked}` : name;
    });

    this.body.querySelectorAll('.overcut-quick-add__value').forEach((node) => {
      const button = /** @type {HTMLButtonElement} */ (node);
      const index = Number(button.dataset.index);
      const value = button.dataset.value || '';
      const available = this.isAvailable(index, value);

      button.setAttribute('aria-pressed', String(this.selection[index] === value));
      button.disabled = !available;
      button.classList.toggle('overcut-quick-add__value--unavailable', !available);
    });

    const variant = this.variant;

    if (this.priceEl && variant) this.priceEl.textContent = money(variant.price);
    if (this.imageEl && variant?.featured_image?.src) this.imageEl.src = variant.featured_image.src;

    if (this.submitEl) {
      const ready = Boolean(variant && variant.available);
      this.submitEl.disabled = !ready;
      this.submitEl.textContent = ready
        ? `${this.dataset.labelAdd} ${money(variant.price)}`
        : this.dataset.labelChoose || '';
    }

    if (variant && !variant.available) this.setHint(this.dataset.hintUnavailable || '', true);
    else if (variant) this.setHint(this.dataset.hintReady || '');
    else this.setHint(this.dataset.hintChoose || '');
  }

  /**
   * Builds the toast wording, e.g. "T-shirt Citroën 2CV, Écru / M".
   * @param {{ title?: string, public_title?: string }} variant
   */
  toastLabel(variant) {
    const name = this.product?.title || '';
    const option = variant.public_title || (variant.title === 'Default Title' ? '' : variant.title) || '';
    return option ? `${name}, ${option}` : name;
  }

  async submit() {
    const variant = this.variant;
    if (!variant || !this.submitEl) return;

    const label = this.submitEl.textContent;
    this.submitEl.disabled = true;
    this.submitEl.textContent = this.dataset.labelPending || label;

    try {
      const root = window.Shopify?.routes?.root || '/';

      // Ask for the cart sections the theme renders so the drawer and line items
      // can be morphed in place. This is what lets us drop the page reload.
      const sectionIds = new Set();
      for (const node of document.querySelectorAll('cart-items-component')) {
        if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.add(node.dataset.sectionId);
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections: Array.from(sectionIds).join(','),
        }),
      });

      if (!response.ok) throw new Error('add failed');

      const added = await response.json();

      // Relecture du panier volontairement TOLERANTE aux pannes : la ligne est
      // deja au panier a ce stade. Si elle partait au catch, on afficherait
      // "L'ajout a echoue" et le visiteur ajouterait le produit une seconde fois.
      let cart = null;
      try {
        const res = await fetch(`${root}cart.js`);
        if (res.ok) cart = await res.json();
      } catch {
        /* la pastille se recalera au prochain chargement */
      }

      this.submitEl.textContent = this.dataset.labelAdded || label;
      document.dispatchEvent(new CustomEvent('overcut-quick-add:added', { bubbles: true }));

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, SHEET_ID, {
          source: QUICK_ADD_SOURCE,
          // cart-icon.js traite un itemCount venant d'une source autre que le
          // formulaire produit comme un TOTAL absolu : un 0 de repli effacerait
          // la pastille. On repart donc du compteur affiche, incremente de 1.
          itemCount: cart?.item_count ?? cartBubbleCount() + 1,
          variantId: String(variant.id),
          sections: added.sections,
        })
      );

      document.dispatchEvent(
        new CustomEvent('overcut-toast:show', {
          bubbles: true,
          detail: { text: this.toastLabel(variant) },
        })
      );

      setTimeout(() => {
        this.close();
        if (!this.submitEl) return;
        this.submitEl.disabled = false;
        this.submitEl.textContent = label;
      }, 500);
    } catch {
      this.setHint(this.dataset.hintError || '', true);
      this.submitEl.disabled = false;
      this.submitEl.textContent = label;
    }
  }
}

/** Lit la pastille panier de l'en-tete, pour ne jamais la remettre a zero. */
function cartBubbleCount() {
  const el = document.querySelector('cart-icon .cart-bubble__text-count');
  return parseInt(el?.textContent?.trim() || '0', 10) || 0;
}

/** Card button that opens the sheet. */
class OvercutQuickAddButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      document.dispatchEvent(
        new CustomEvent('overcut-quick-add:open', { detail: { handle: this.dataset.productHandle } })
      );
    });
  }
}

if (!customElements.get('overcut-quick-add-sheet')) {
  customElements.define('overcut-quick-add-sheet', OvercutQuickAddSheet);
}
if (!customElements.get('overcut-quick-add')) {
  customElements.define('overcut-quick-add', OvercutQuickAddButton);
}

export { SHEET_ID };
